import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchLabels, deleteLabel } from '../../../api/labels';

/**
 * useLabelList - 标签列表管理 Hook
 *
 * v2（检索筛选版）：
 * 后端 /labels 仅支持 pageNum/pageSize，没有搜索参数。
 * 因此改为「分批拉取全量 → 前端过滤 → 前端分页」：
 *   - 单次请求 pageSize=FETCH_SIZE（200），若 total 更大则继续翻页，最多 MAX_FETCH_PAGES 页
 *   - 过滤条件：名称关键词 / 状态 / 创建时间范围
 *   - 每页展示 PAGE_SIZE（10）条
 * 这样 200+ 标签的数据量（约几百 KB）可以一次装进内存，搜索能在全量范围内生效。
 */

const FETCH_SIZE = 200;
const MAX_FETCH_PAGES = 10; // 兜底：最多取 2000 条，防止异常情况下无限翻页
const PAGE_SIZE = 10;

const EMPTY_FILTERS = { keyword: '', status: 'all', dateFrom: '', dateTo: '' };

/** 从多种可能的响应结构里取列表 */
function extractList(result) {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.list)) return result.list;
  if (Array.isArray(result?.records)) return result.records;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.data?.list)) return result.data.list;
  if (Array.isArray(result?.data?.records)) return result.data.records;
  return [];
}

/** 从多种可能的响应结构里取总条数 */
function extractTotal(result, fallback) {
  const candidates = [result?.total, result?.totalCount, result?.data?.total, result?.data?.totalCount];
  for (const c of candidates) {
    if (typeof c === 'number' && !Number.isNaN(c)) return c;
  }
  return fallback;
}

/** 取标签的展示时间（创建时间优先，其次更新时间） */
function labelTime(label) {
  const raw = label?.createTime || label?.createdAt || label?.updateTime || label?.updatedAt;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** 取标签状态（兼容 published / draft / DRAFT 等写法） */
function labelStatus(label) {
  const s = String(label?.status || 'draft').toLowerCase();
  if (s === 'published' || s === 'publish' || s === 'online') return 'published';
  return 'draft';
}

export function useLabelList() {
  const [allLabels, setAllLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [pageNum, setPageNum] = useState(1);
  const [serverTruncated, setServerTruncated] = useState(false); // 后端是否限制了单次返回条数

  // 加载全部标签（分批）
  const loadAll = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      let collected = [];
      let page = 1;
      let total = Infinity;

      while (collected.length < total && page <= MAX_FETCH_PAGES) {
        // eslint-disable-next-line no-await-in-loop
        const result = await fetchLabels({ pageNum: page, pageSize: FETCH_SIZE });
        const list = extractList(result);
        total = extractTotal(result, collected.length + list.length);

        collected = collected.concat(list);

        if (list.length === 0) break;
        if (list.length < FETCH_SIZE && collected.length >= total) break;
        page += 1;
      }

      // 若最后仍拿不全，标记数据被后端截断（此时搜索仅覆盖已拉到的部分）
      setServerTruncated(Number.isFinite(total) && collected.length < total);
      setAllLabels(collected);
    } catch (err) {
      console.error('Failed to load labels:', err);
      setError(err.message || '加载标签列表失败');
      setAllLabels([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 首次加载
  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // 过滤后的结果
  const filteredLabels = useMemo(() => {
    const kw = filters.keyword.trim().toLowerCase();
    const { status, dateFrom, dateTo } = filters;

    // 日期边界：dateTo 当天 23:59:59
    const fromTime = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
    const toTime = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;

    return allLabels.filter((label) => {
      if (kw) {
        const name = String(label?.labelName || label?.name || '').toLowerCase();
        if (!name.includes(kw)) return false;
      }
      if (status !== 'all' && labelStatus(label) !== status) return false;
      if (fromTime !== null || toTime !== null) {
        const t = labelTime(label);
        if (!t) return false;
        const ts = t.getTime();
        if (fromTime !== null && ts < fromTime) return false;
        if (toTime !== null && ts > toTime) return false;
      }
      return true;
    });
  }, [allLabels, filters]);

  // 过滤结果的分页
  const total = filteredLabels.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePageNum = Math.min(pageNum, totalPages);
  const pageLabels = useMemo(
    () => filteredLabels.slice((safePageNum - 1) * PAGE_SIZE, safePageNum * PAGE_SIZE),
    [filteredLabels, safePageNum]
  );

  // 统计（用于筛选栏上的数量提示）
  const counts = useMemo(() => {
    let published = 0;
    let draft = 0;
    allLabels.forEach((l) => {
      if (labelStatus(l) === 'published') published += 1;
      else draft += 1;
    });
    return { all: allLabels.length, published, draft };
  }, [allLabels]);

  const updateFilters = useCallback((patch) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPageNum(1); // 条件变化回到第一页
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPageNum(1);
  }, []);

  const hasActiveFilter = Boolean(
    filters.keyword || filters.status !== 'all' || filters.dateFrom || filters.dateTo
  );

  // 删除标签
  const handleDelete = useCallback(
    async (labelId) => {
      if (!window.confirm('确定要删除这个标签吗？删除后无法恢复。')) {
        return false;
      }
      try {
        setIsLoading(true);
        await deleteLabel(labelId);
        await loadAll();
        return true;
      } catch (err) {
        console.error('Failed to delete label:', err);
        setError(err.message || '删除标签失败');
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [loadAll]
  );

  const changePage = useCallback((next) => {
    setPageNum((prev) => {
      const target = typeof next === 'number' ? next : prev + 1;
      return Math.max(1, target);
    });
  }, []);

  const refresh = useCallback(() => {
    loadAll();
  }, [loadAll]);

  return {
    // 兼容原有接口
    labels: pageLabels,
    isLoading,
    error,
    pagination: {
      pageNum: safePageNum,
      pageSize: PAGE_SIZE,
      total,
      totalPages,
    },
    loadLabels: loadAll,
    handleDelete,
    changePage,
    refresh,
    // 新增：筛选相关
    filters,
    updateFilters,
    resetFilters,
    hasActiveFilter,
    counts,
    totalCount: allLabels.length,
    serverTruncated,
  };
}
